import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Entity } from '@ag-common-lib/public-api';
import {
  getReportUnavailableReason,
  type ReportUnavailableReason,
} from '../../../components/report-unavailable/report-unavailable.component';
import { AgencyService } from '../../../services/agency.service';
import { ProductionHistoryService } from '../../../services/production-history.service';
import { ReportFilterOptionsService } from '../../../services/report-filter-options.service';
import {
  getReportFilterPlaceholder,
  toReportFilters,
  type ReportFilterOption,
} from '../../../utils/report-filter-options.utils';
import { REPORT_SCOPE_LABELS, canSeeReportScope, parseReportScopeParam } from '../../../utils/report-sections.utils';
import { getLastPassedFriday, normalizeAsOfDate, toIsoDate } from '../../../utils/reporting-comparison-date.utils';
import { PRODUCTION_DASHBOARD_CONTEXT } from '../production-dashboard-context';
import {
  DEFAULT_PRODUCTION_HORIZON,
  EMPTY_HISTORY_DATA,
  Granularity,
  PRODUCTION_PROJECTION_MODEL_LABELS,
  ProductionHorizon,
  ProductionProjectionModel,
  type ProductionHistoryDashboardData,
  type ProductionHistoryDashboardDefaults,
  type ProductionHistoryDashboardGridRow,
  type ProductionHistoryGridSummary,
  type HistoryGranularity,
} from './production-history-dashboard.models';
import {
  ProductionHistoryExportService,
  type ProductionHistoryExportColumn,
  type ProductionHistoryExportSection,
} from './production-history-export.service';
import {
  getCurrentYearAnnualValue,
  getCurrentYearMonthSeries,
  getCurrentYearQuarterValue,
  getAverageMonthlyProductionRate,
  PRODUCTION_HISTORY_AVERAGE_LABEL,
  hasProductionHistoryProjectionContract,
  type HistoryHorizonContext,
} from './production-history-horizon';
import {
  applyRevenueAnalyticsReportingLag,
  parseIsoDateParts,
  PRODUCTION_HISTORY_REPORTING_LAG_WEEKS,
  type ProductionHistoryDateParts,
} from './production-history-as-of';
import { getQuarterLabel, getQuarterNumberFromLabel } from './production-history-quarters.utils';
import {
  buildProductionTrackingAsOfLabel,
  buildProductionTrackingTitle,
  getProductionTrackingSubject,
} from './production-history-title';
import {
  PROJECTION_METHOD_OPTIONS,
  type ProjectionMethodOption,
} from '../../../components/projection-method-select/projection-method-options';
import type { SelectionChangedEvent } from 'devextreme/ui/list';
import type {
  DashboardRevenueProjectionMethodDto,
  ProductionHistoryHttpQuery,
  ProductionHistoryMonthlyItemResponse,
  ProductionHistoryPremiumSource,
  ProductionHistoryProjectionFields,
  ProductionHistoryQuarterlyItemResponse,
  ProductionHistoryResponseMeta,
  ProductionHistoryYearlyItemResponse,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import {
  ReportFilterOptionsPages,
  ReportingComparisonScopes,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { from, map, of, switchMap } from 'rxjs';

type HistoryGridColumn = {
  dataField?: string;
  caption: string;
  columns?: HistoryGridColumn[];
  dataType?: 'number' | 'string';
  format?: { type: 'currency' | 'percent'; precision: number };
  cellTemplate?: string;
  minWidth?: number;
  width?: number;
  fixed?: boolean;
  fixedPosition?: 'left' | 'right';
  alignment?: 'left' | 'center' | 'right';
  allowResizing?: boolean;
  cssClass?: string;
};

type HistoryGridSummaryItem = {
  name: string;
  column: string;
  valueFormat?: { type: 'currency' | 'percent'; precision: number };
};

type CustomSummaryOptions = {
  name?: string;
  summaryProcess: 'start' | 'calculate' | 'finalize';
  totalValue?: string | number | null;
};

type HistorySection = {
  id: HistoryGranularity;
  title: string;
};

type HistoryExportFormat = 'pdf' | 'excel';

/**
 * Everything the grids are derived from, kept raw.
 *
 * The Production Horizon re-shapes all three grids at once, so a horizon or method change has to
 * rebuild them from the API rows rather than patch the mapped output.
 */
type ProductionHistorySource = {
  monthlyItems: ProductionHistoryMonthlyItemResponse[];
  quarterlyItems: ProductionHistoryQuarterlyItemResponse[];
  yearlyItems: ProductionHistoryYearlyItemResponse[];
  lastTransactionDate: string | null;
  meta: ProductionHistoryResponseMeta;
};

type ProjectionMethodMenuItem = {
  id: DashboardRevenueProjectionMethodDto;
  text: string;
  option: ProjectionMethodOption;
};

type HistoryExportMenuItem = {
  id: HistoryExportFormat;
  text: string;
  icon: string;
};

type ProductionHistoryGridCellInfo = {
  data?: ProductionHistoryDashboardGridRow;
  value?: number | null;
  column?: {
    dataField?: string;
  };
};

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const HISTORY_DATA_COLUMN_MIN_WIDTH = 150;
const COMPARISON_COLUMN_WIDTH = 110;
const HISTORY_SPACER_COLUMN_FIELD = '__historySpacer';
const HISTORY_SPACER_COLUMN_CLASS = 'history-dashboard__grid-spacer-column';
const USD_CURRENCY_FORMAT = { type: 'currency' as const, precision: 2 };
const EMPTY_HISTORY_DEFAULTS: ProductionHistoryDashboardDefaults = { data: EMPTY_HISTORY_DATA, summaryData: {} };
const PERCENT_FORMAT = { type: 'percent' as const, precision: 1 };
/**
 * The picker speaks `DashboardRevenueProjectionMethodDto`, the projection math speaks
 * `ProductionProjectionModel`. Same three methods, same wire values — spelled out rather than cast
 * so adding a fourth fails to compile here instead of silently projecting at run rate.
 */
const PROJECTION_MODEL_BY_METHOD: Record<DashboardRevenueProjectionMethodDto, ProductionProjectionModel> = {
  runrate: ProductionProjectionModel.RunRate,
  pace: ProductionProjectionModel.Pace,
  credibility: ProductionProjectionModel.Credibility,
};

@Component({
  selector: 'ag-portal-production-history-dashboard',
  templateUrl: './production-history-dashboard.component.html',
  styleUrls: ['./production-history-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductionHistoryExportService],
})
export class ProductionHistoryDashboardComponent {
  protected readonly historySections: HistorySection[] = [
    { id: Granularity.Monthly, title: 'Monthly' },
    { id: Granularity.Quarterly, title: 'Quarterly' },
    { id: Granularity.Annual, title: 'Projected Annual Production' },
  ];
  /** The same three methods the projection engine offers, as the split button's drop-down items. */
  protected readonly projectionModelItems: ProjectionMethodMenuItem[] = PROJECTION_METHOD_OPTIONS.map(option => ({
    id: option.value,
    text: option.label,
    option,
  }));
  protected readonly horizonDropDownOptions = {
    width: 200,
    wrapperAttr: { class: 'history-dashboard__horizon-drop-down-popup' },
  };
  protected readonly exportMenuItems: HistoryExportMenuItem[] = [
    { id: 'pdf', text: 'PDF', icon: 'pdffile' },
    { id: 'excel', text: 'Excel', icon: 'xlsxfile' },
  ];

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly document = inject(DOCUMENT);
  private readonly reportFilterOptionsService = inject(ReportFilterOptionsService);
  private readonly dashboardContext = inject(PRODUCTION_DASHBOARD_CONTEXT);
  private readonly productionHistoryService = inject(ProductionHistoryService);
  private readonly historyExportService = inject(ProductionHistoryExportService);
  private readonly agencyService = inject(AgencyService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly paidFileAsOfDateSig = toSignal(this.dashboardContext.paidFileAsOfData$, { initialValue: null });
  protected readonly isLoadingSig = signal(false);
  protected readonly isExportingSig = signal(false);
  protected readonly loadErrorMessageSig = signal<string | null>(null);
  /** Set when the API refused the history: nothing to show, rather than a failure to retry. */
  protected readonly unavailableReasonSig = signal<ReportUnavailableReason | null>(null);
  /** Whether this scope's history has loaded; the header's controls wait for it, so a refusal never flashes them. */
  protected readonly hasLoadedSig = signal(false);
  protected readonly Granularity = Granularity;
  /** Same three methods as Revenue Projection, and the same picker; Production History starts on run rate. */
  protected readonly selectedProjectionMethodSig = signal<DashboardRevenueProjectionMethodDto>('runrate');
  protected readonly selectedHorizonSig = signal(DEFAULT_PRODUCTION_HORIZON);
  protected readonly selectedReportFilterValuesSig = signal<string[]>([]);
  protected readonly selectedYearsSig = signal<number[]>([]);
  private readonly gridLayoutVersionSig = signal(0);
  private readonly currentYear = new Date().getFullYear();
  private readonly historySourceSig = signal<ProductionHistorySource | null>(null);
  private reportFilterReloadTimer: ReturnType<typeof setTimeout> | null = null;
  private lastLoadedScope: ReportingComparisonScope | null = null;
  /** The year picker is seeded from the first load only, so a filter or horizon reload never re-checks years. */
  private yearSelectionInitialized = false;
  /** Guards against a slower earlier load landing on top of a newer one. */
  private loadGeneration = 0;
  protected isProjectionHelpVisible = false;
  protected projectionHelpTarget: EventTarget | Element | null = null;
  protected projectionHelpOption: ProjectionMethodOption | null = null;
  private projectionHelpCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Every figure comes from the reporting API, in every scope and for every year. */
  private readonly historyDefaultsSig = computed<ProductionHistoryDashboardDefaults>(() => {
    const source = this.historySourceSig();
    if (!source) {
      return EMPTY_HISTORY_DEFAULTS;
    }

    return this.shapeHistoryDefaults(
      this.buildApiHistoryDefaults(
        source.monthlyItems,
        source.quarterlyItems,
        source.yearlyItems,
        source.lastTransactionDate,
      ),
      source.lastTransactionDate,
    );
  });
  protected readonly historyDataSig = computed(() => this.historyDefaultsSig().data);
  protected readonly historySummaryDataSig = computed(() => this.historyDefaultsSig().summaryData);
  protected readonly availableYearsSig = computed(() => this.getHistoryYearRange(this.historyDataSig()));
  protected readonly selectableYearsSig = computed(() =>
    this.availableYearsSig().filter(year => year !== this.currentYear),
  );
  /**
   * The years every grid shows. The annual grid used to bypass this and take the whole range, so
   * clearing a year dropped it from Monthly and Quarterly and left it standing in Annual.
   */
  protected readonly visibleYearColumnsSig = computed(() => {
    const selectedYears = new Set(this.selectedYearsSig());

    return this.availableYearsSig().filter(year => year === this.currentYear || selectedYears.has(year));
  });
  protected readonly yearsDropDownOptions = {
    width: 220,
    wrapperAttr: { class: 'history-dashboard__years-drop-down-popup' },
  };

  protected readonly currentScopeSig = toSignal(
    this.route.paramMap.pipe(map(params => parseReportScopeParam(params.get('scope')))),
    { initialValue: parseReportScopeParam(this.route.snapshot.paramMap.get('scope')) },
  );

  protected readonly scopeLabelSig = computed(() => {
    const scope = this.currentScopeSig();
    return scope ? REPORT_SCOPE_LABELS[scope] : '';
  });

  /** Whether the permission map has arrived; until then a hidden scope is unknown, not refused. */
  protected readonly isAccessKnownSig = computed(() => this.dashboardContext.permissionsSig() !== null);

  protected readonly isVisibleSig = computed(() =>
    canSeeReportScope(this.dashboardContext, this.currentScopeSig(), Entity.reportsViewerProductionHistory),
  );

  /** An MGA page covers one agency, so there is nothing to narrow; the others offer the filter. */
  protected readonly showMgaFilterSig = computed(
    () => this.currentScopeSig() !== null && this.currentScopeSig() !== ReportingComparisonScopes.mga,
  );

  /**
   * What this viewer may narrow by: every RMD and MGA company-wide, an RMD's own agencies on
   * their page. Fetched only while the filter is shown, so scoped pages ask for nothing.
   */
  protected readonly reportFilterOptionsSig = toSignal(
    toObservable(
      computed(() => ({
        scope: this.showMgaFilterSig() ? this.currentScopeSig() : null,
        agent: this.dashboardContext.viewedAgentSig(),
      })),
    ).pipe(
      // The agent is only a trigger: the API reads the viewed agent from the request.
      switchMap(({ scope }) =>
        scope ? this.reportFilterOptionsService.optionsFor(scope, ReportFilterOptionsPages.productionHistory) : of([]),
      ),
    ),
    { initialValue: [] as ReportFilterOption[] },
  );

  protected readonly reportFilterPlaceholderSig = computed(() => getReportFilterPlaceholder(this.currentScopeSig()));

  /** The viewed agent's own MGA, which an MGA page is named after. Looked up on that page only. */
  private readonly viewedMgaNameSig = toSignal(
    toObservable(
      computed(() =>
        this.currentScopeSig() === ReportingComparisonScopes.mga
          ? String(this.dashboardContext.viewedAgentSig()?.p_mga_id ?? '').trim()
          : '',
      ),
    ).pipe(
      switchMap(mgaId =>
        mgaId
          ? from(
              this.agencyService
                .getAgencyByAgencyId(mgaId)
                .then(agency => agency?.name ?? null)
                .catch(() => null),
            )
          : of(null),
      ),
    ),
    { initialValue: null as string | null },
  );

  /** "Production Tracking - <who>": the page, the PDF and the workbook all carry it (TASKID-3477). */
  protected readonly pageTitleSig = computed(() =>
    buildProductionTrackingTitle(
      getProductionTrackingSubject({
        scope: this.currentScopeSig(),
        selectedValues: this.selectedReportFilterValuesSig(),
        options: this.reportFilterOptionsSig(),
        mgaName: this.viewedMgaNameSig(),
      }),
    ),
  );

  /** The paid file's as-of date, or the last transaction on file when the app has none. */
  protected readonly asOfLabelSig = computed(() =>
    buildProductionTrackingAsOfLabel(
      this.toPlainIsoDate(this.paidFileAsOfDateSig()) ??
        this.toPlainIsoDate(this.historySourceSig()?.lastTransactionDate),
    ),
  );

  /**
   * Projection needs something to project from. With no current-year production on file the
   * control is hidden rather than disabled: there is no view behind it to offer.
   */
  protected readonly isProjectionAvailableSig = computed(() => this.hasCurrentYearActualProduction());

  protected readonly isActualHorizonSig = computed(
    () => this.selectedHorizonSig() === ProductionHorizon.Actual || !this.isProjectionAvailableSig(),
  );

  protected readonly isProjectionHorizonSig = computed(
    () => this.selectedHorizonSig() === ProductionHorizon.Projection && this.isProjectionAvailableSig(),
  );

  /**
   * One two-segment control: Actual on the left, Projection on the right as a split button whose
   * drop-down picks the model. Reading the mode off the pressed segment beats reading it out of a
   * label, and it keeps the choice of model one click away instead of behind a second control.
   */
  protected readonly projectionHorizonLabelSig = computed(() => this.getProjectionHorizonLabel());

  constructor() {
    // Loads when the scope changes or becomes visible (the permission map can arrive late).
    effect(
      () => {
        const scope = this.currentScopeSig();
        if (scope && this.isVisibleSig()) {
          untracked(() => void this.loadProductionHistory(scope));
        }
      },
      { allowSignalWrites: true },
    );
  }

  protected onActualHorizonClick(): void {
    if (this.selectedHorizonSig() === ProductionHorizon.Actual) {
      return;
    }

    this.selectedHorizonSig.set(ProductionHorizon.Actual);
    void this.reloadForHorizonChange();
  }

  protected onProjectionHorizonClick(): void {
    if (!this.isProjectionAvailableSig() || this.selectedHorizonSig() === ProductionHorizon.Projection) {
      return;
    }

    this.selectedHorizonSig.set(ProductionHorizon.Projection);
    void this.reloadForHorizonChange();
  }

  /** Picking a model also turns the projection on: that is the only reason to pick one. */
  protected onProjectionModelClick(event: { itemData?: ProjectionMethodMenuItem }): void {
    const method = event.itemData?.id;
    if (!method || !this.isProjectionAvailableSig()) {
      return;
    }

    const isSameMethod = this.selectedProjectionMethodSig() === method;
    const isAlreadyProjection = this.selectedHorizonSig() === ProductionHorizon.Projection;
    if (isSameMethod && isAlreadyProjection) {
      return;
    }

    this.selectedProjectionMethodSig.set(method);
    this.selectedHorizonSig.set(ProductionHorizon.Projection);
    void this.reloadForHorizonChange();
  }

  protected openProjectionHelp(event: MouseEvent, item: ProjectionMethodMenuItem): void {
    this.cancelProjectionHelpClose();
    this.projectionHelpTarget = event.currentTarget ?? event.target;
    this.projectionHelpOption = item.option;
    this.isProjectionHelpVisible = true;
  }

  protected holdProjectionHelp(): void {
    this.cancelProjectionHelpClose();
  }

  protected closeProjectionHelp(): void {
    this.cancelProjectionHelpClose();
    this.projectionHelpCloseTimer = setTimeout(() => {
      this.isProjectionHelpVisible = false;
      this.projectionHelpCloseTimer = null;
      this.changeDetectorRef.markForCheck();
    }, 250);
  }

  protected closeProjectionHelpNow(): void {
    this.cancelProjectionHelpClose();
    this.isProjectionHelpVisible = false;
  }

  private cancelProjectionHelpClose(): void {
    if (this.projectionHelpCloseTimer !== null) {
      clearTimeout(this.projectionHelpCloseTimer);
      this.projectionHelpCloseTimer = null;
    }
  }

  /**
   * The horizon and the method go to the API, which projects server-side once it supports them.
   * Until then the reload is what re-runs the client-side fallback over the same rows.
   */
  private reloadForHorizonChange(): Promise<void> {
    const scope = this.currentScopeSig();

    return scope ? this.loadProductionHistory(scope) : Promise.resolve();
  }

  protected getGridRows(granularity: HistoryGranularity) {
    return this.historyDataSig()[granularity];
  }

  protected getGridColumns(granularity: HistoryGranularity, gridShell?: HTMLElement | null): HistoryGridColumn[] {
    const hasComparisonColumns = this.hasComparisonColumns(granularity);
    const yearColumns: HistoryGridColumn[] = this.visibleYearColumnsSig().map(year => {
      const productionColumn: HistoryGridColumn = {
        dataField: this.getYearField(year),
        caption: year === this.currentYear && hasComparisonColumns ? 'Production' : `${year}`,
        dataType: 'number' as const,
        ...(granularity === Granularity.Annual ? {} : { format: USD_CURRENCY_FORMAT }),
        minWidth: HISTORY_DATA_COLUMN_MIN_WIDTH,
        width: HISTORY_DATA_COLUMN_MIN_WIDTH,
        ...(year === this.currentYear && !hasComparisonColumns
          ? {
              fixed: true,
              fixedPosition: 'left' as const,
            }
          : {}),
        ...(granularity === Granularity.Monthly
          ? {
              cellTemplate: 'monthlyProductionCellTemplate',
            }
          : {}),
        ...(granularity === Granularity.Quarterly
          ? {
              cellTemplate: 'quarterlyProductionCellTemplate',
            }
          : {}),
        ...(granularity === Granularity.Annual
          ? {
              cellTemplate: 'annualValueCellTemplate',
            }
          : {}),
        alignment: 'right' as const,
        allowResizing: true,
      };

      if (year !== this.currentYear || !hasComparisonColumns) {
        return productionColumn;
      }

      return {
        caption: `${year}`,
        minWidth: HISTORY_DATA_COLUMN_MIN_WIDTH + COMPARISON_COLUMN_WIDTH * 2,
        fixed: true,
        fixedPosition: 'left' as const,
        alignment: 'center' as const,
        allowResizing: true,
        columns: [
          {
            dataField: 'priorPeriodChange',
            caption: this.getPriorPeriodChangeCaption(granularity),
            dataType: 'number',
            format: PERCENT_FORMAT,
            minWidth: COMPARISON_COLUMN_WIDTH,
            width: COMPARISON_COLUMN_WIDTH,
            alignment: 'right',
            allowResizing: true,
            cellTemplate: 'comparisonPercentCellTemplate',
          },
          {
            dataField: 'currentYearChange',
            caption: `% change for the\u00A0current\u00A0year over last\u00A0year`,
            dataType: 'number',
            format: PERCENT_FORMAT,
            minWidth: COMPARISON_COLUMN_WIDTH,
            width: COMPARISON_COLUMN_WIDTH,
            alignment: 'right',
            allowResizing: true,
            cellTemplate: 'comparisonPercentCellTemplate',
          },
          productionColumn,
        ],
      };
    });

    const columns: HistoryGridColumn[] = [
      {
        dataField: 'label',
        caption: this.getPeriodColumnCaption(granularity),
        dataType: 'string',
        width: 170,
        minWidth: HISTORY_DATA_COLUMN_MIN_WIDTH,
        fixed: true,
        fixedPosition: 'left',
        alignment: 'left',
        allowResizing: false,
      },
      ...yearColumns,
    ];

    if (this.shouldShowSpacerColumn(granularity, gridShell)) {
      columns.push(this.getSpacerColumn(granularity, gridShell));
    }

    return columns;
  }

  protected getGridSummaryItems(granularity: HistoryGranularity): HistoryGridSummaryItem[] {
    const summary = this.getGridSummary(granularity);
    if (!summary) {
      return [];
    }

    const items: HistoryGridSummaryItem[] = [
      {
        name: 'summary_label',
        column: 'label',
      },
      ...this.visibleYearColumnsSig().map(year => ({
        name: `summary_${this.getYearField(year)}`,
        column: this.getYearField(year),
        valueFormat: USD_CURRENCY_FORMAT,
      })),
    ];

    if (this.hasComparisonColumns(granularity)) {
      items.push(
        {
          name: 'summary_priorPeriodChange',
          column: 'priorPeriodChange',
          valueFormat: PERCENT_FORMAT,
        },
        {
          name: 'summary_currentYearChange',
          column: 'currentYearChange',
          valueFormat: PERCENT_FORMAT,
        },
      );
    }

    return items;
  }

  protected onYearListSelectionChanged(event: SelectionChangedEvent): void {
    const selectableYears = new Set(this.selectableYearsSig());
    const selectedYears = (event.component.option('selectedItemKeys') as number[])
      .filter(year => selectableYears.has(year))
      .sort((left, right) => right - left);

    this.selectedYearsSig.set(selectedYears);
  }

  /** Applies itself, debounced, so picking several agencies asks the API once. */
  protected onReportFilterValueChanged(value: string[]): void {
    this.selectedReportFilterValuesSig.set(value);

    if (this.reportFilterReloadTimer) {
      clearTimeout(this.reportFilterReloadTimer);
    }

    this.reportFilterReloadTimer = setTimeout(() => {
      this.reportFilterReloadTimer = null;
      const scope = this.currentScopeSig();
      if (scope) {
        void this.loadProductionHistory(scope);
      }
    }, 1000);
  }

  protected async exportDashboard(format: HistoryExportFormat): Promise<void> {
    if (this.isExportingSig()) {
      return;
    }

    const sections = this.getExportSections();
    if (!sections.some(section => section.rows.length)) {
      return;
    }

    this.isExportingSig.set(true);

    try {
      const exportArgs = {
        fileName: this.buildExportFileName(),
        title: this.pageTitleSig(),
        periodLabel: this.asOfLabelSig(),
        sections,
      };

      if (format === 'pdf') {
        await this.historyExportService.exportPdf(exportArgs);
      } else {
        await this.historyExportService.exportExcel(exportArgs);
      }
    } finally {
      this.isExportingSig.set(false);
    }
  }

  protected onExportItemClick(event: { itemData?: HistoryExportMenuItem }): void {
    const format = event.itemData?.id;
    if (!format) {
      return;
    }

    void this.exportDashboard(format);
  }

  protected getYearField(year: number): string {
    return `year_${year}`;
  }

  protected calculateGridSummary =
    (granularity: HistoryGranularity) =>
    (options: CustomSummaryOptions): void => {
      if (options.summaryProcess !== 'finalize' || !options.name) {
        return;
      }

      const summary = this.getGridSummary(granularity);
      if (!summary) {
        options.totalValue = null;
        return;
      }

      if (options.name === 'summary_label') {
        options.totalValue = summary.label;
        return;
      }

      const fieldName = options.name.replace(/^summary_/, '');
      const summaryValue = summary[fieldName];
      options.totalValue = typeof summaryValue === 'string' || typeof summaryValue === 'number' ? summaryValue : null;
    };

  protected getSectionTitle(granularity: HistoryGranularity): string {
    if (granularity === Granularity.Annual) {
      return this.isProjectionHorizonSig() ? 'Projected Annual Production' : 'Annual Production';
    }

    return this.historySections.find(section => section.id === granularity)?.title ?? 'Monthly';
  }

  private getSelectedProjectionMethodLabel(): string {
    return (
      PROJECTION_METHOD_OPTIONS.find(option => option.value === this.selectedProjectionMethodSig())?.label ?? 'Run rate'
    );
  }

  private getProjectionHorizonLabel(): string {
    return `Projection (${this.getSelectedProjectionMethodLabel()})`;
  }

  /**
   * The note under a grid, for the screen and for the exports alike.
   *
   * It is per grid, and driven by the cells rather than by the granularity: a table earns the note
   * only once it actually holds a projected figure. A finished quarter is actual production out of
   * the master file, and calling that a projection is wrong.
   */
  protected getTableFootnote(granularity: HistoryGranularity): string | undefined {
    if (!this.hasProjectionCells(granularity)) {
      return undefined;
    }

    return (
      `Blue cells contain projected production based on ${this.getSelectedProjectionMethodLabel()}. ` +
      `Actual paid data is used through ${this.formatFootnoteDate(this.getTrustedPaidThroughIsoDate())}.`
    );
  }

  private hasProjectionCells(granularity: HistoryGranularity): boolean {
    return this.getGridRows(granularity).some(row => this.rowHasProjectionCells(row));
  }

  private rowHasProjectionCells(row: ProductionHistoryDashboardGridRow): boolean {
    if (row.priorPeriodChangeIsProjectionDerived || row.currentYearChangeIsProjectionDerived) {
      return true;
    }

    return Object.values(row.isProjectionHighlightedByYear ?? {}).some(Boolean);
  }

  protected getMonthlyProductionCellClass(cellInfo: ProductionHistoryGridCellInfo): string {
    // A projected figure is not a ranking of anything, so the projection tint wins over the medals.
    if (this.isProjectionHighlightedCell(cellInfo)) {
      return 'history-dashboard__monthly-cell history-dashboard__monthly-cell--projected';
    }

    const rank = this.getMonthlyProductionCellRank(cellInfo);
    if (rank === 1 || rank === 2 || rank === 3) {
      return `history-dashboard__monthly-cell history-dashboard__monthly-cell--rank-${rank}`;
    }

    return 'history-dashboard__monthly-cell';
  }

  protected isGlobalTopProducingMonthCell(cellInfo: ProductionHistoryGridCellInfo): boolean {
    const year = this.getCellYear(cellInfo);
    if (!year) {
      return false;
    }

    return cellInfo.data?.globalTopMonthByYear?.[year] === true;
  }

  protected getQuarterlyProductionCellClass(cellInfo: ProductionHistoryGridCellInfo): string {
    if (this.isProjectionHighlightedCell(cellInfo)) {
      return 'history-dashboard__quarterly-cell history-dashboard__quarterly-cell--projected';
    }

    const rank = this.getQuarterlyProductionCellRank(cellInfo);
    if (rank === 1) {
      return 'history-dashboard__quarterly-cell history-dashboard__quarterly-cell--rank-1';
    }

    return 'history-dashboard__quarterly-cell';
  }

  protected isGlobalTopProducingQuarterCell(cellInfo: ProductionHistoryGridCellInfo): boolean {
    const year = this.getCellYear(cellInfo);
    if (!year) {
      return false;
    }

    return cellInfo.data?.globalTopQuarterByYear?.[year] === true;
  }

  protected getAnnualCellClass(cellInfo: ProductionHistoryGridCellInfo): string {
    if (this.isProjectionHighlightedCell(cellInfo)) {
      return 'history-dashboard__annual-cell history-dashboard__annual-cell--projected';
    }

    return 'history-dashboard__annual-cell';
  }

  protected getComparisonPercentCellClass(cellInfo: ProductionHistoryGridCellInfo): string {
    if (this.isProjectionDerivedPercentCell(cellInfo)) {
      return 'history-dashboard__percent-cell history-dashboard__percent-cell--projected';
    }

    return 'history-dashboard__percent-cell';
  }

  /** The tint says a figure is not settled production; the tooltip says which kind it is. */
  protected getProjectionCellTitle(cellInfo: ProductionHistoryGridCellInfo): string | undefined {
    if (cellInfo.value === null || cellInfo.value === undefined) {
      return undefined;
    }

    if (this.isProjectionDerivedPercentCell(cellInfo)) {
      return 'Projection-derived';
    }

    if (!this.isProjectionHighlightedCell(cellInfo)) {
      return undefined;
    }

    return this.isAnnualPercentCell(cellInfo) ? 'Projection-derived' : 'Projected value';
  }

  protected isProjectionHighlightedCell(cellInfo: ProductionHistoryGridCellInfo): boolean {
    const year = this.getCellYear(cellInfo);
    if (year && year < this.currentYear) {
      return false;
    }

    if (this.isProjectionDerivedPercentCell(cellInfo)) {
      return true;
    }

    if (!year) {
      return false;
    }

    return cellInfo.data?.isProjectionHighlightedByYear?.[year] === true;
  }

  protected isProjectionDerivedPercentCell(cellInfo: ProductionHistoryGridCellInfo): boolean {
    const field = cellInfo.column?.dataField;
    if (field === 'priorPeriodChange') {
      return cellInfo.data?.priorPeriodChangeIsProjectionDerived === true;
    }

    if (field === 'currentYearChange') {
      return cellInfo.data?.currentYearChangeIsProjectionDerived === true;
    }

    return false;
  }

  protected isAnnualPercentCell(cellInfo: ProductionHistoryGridCellInfo): boolean {
    return cellInfo.data?.valueKind === 'percent';
  }

  private hasComparisonColumns(granularity: HistoryGranularity): boolean {
    return granularity === Granularity.Monthly || granularity === Granularity.Quarterly;
  }

  private getPeriodColumnCaption(granularity: HistoryGranularity): string {
    if (granularity === Granularity.Quarterly) {
      return 'Quarterly';
    }

    if (granularity === Granularity.Annual) {
      return '';
    }

    return 'Monthly';
  }

  private getPriorPeriodChangeCaption(granularity: HistoryGranularity): string {
    if (granularity === Granularity.Quarterly) {
      return `% change over the\u00A0prior\u00A0quarter`;
    }

    if (granularity === Granularity.Annual) {
      return `% change over the\u00A0prior\u00A0year`;
    }

    return `% change over the\u00A0prior\u00A0month`;
  }

  private getGridSummary(granularity: HistoryGranularity): ProductionHistoryGridSummary | null {
    return this.historySummaryDataSig()[granularity] ?? null;
  }

  protected goToMyReports(): void {
    void this.router.navigate(this.dashboardContext.myReportsRouteCommands);
  }

  protected toggleFullScreen = async (container: HTMLDivElement): Promise<void> => {
    if (this.document.fullscreenElement) {
      await this.document.exitFullscreen();

      return;
    }

    this.document
      .querySelectorAll('.ag-full-screen-app')
      .forEach(element => element.classList.remove('ag-full-screen-app'));
    await this.document?.documentElement?.requestFullscreen();
    container.classList.add('ag-full-screen-app');
    this.refreshGridLayout();
  };

  @HostListener('document:fullscreenchange')
  protected fullscreenChanged(): void {
    if (!this.document.fullscreenElement) {
      this.document
        .querySelectorAll('.ag-full-screen-app')
        .forEach(element => element.classList.remove('ag-full-screen-app'));
    }

    this.refreshGridLayout();
  }

  @HostListener('window:resize')
  protected windowResized(): void {
    this.refreshGridLayout();
  }

  private shouldShowSpacerColumn(granularity: HistoryGranularity, gridShell?: HTMLElement | null): boolean {
    this.gridLayoutVersionSig();

    const availableWidth = Math.floor(gridShell?.clientWidth ?? 0);
    if (!availableWidth) {
      return false;
    }

    return availableWidth - this.getGridContentWidth(granularity) >= HISTORY_DATA_COLUMN_MIN_WIDTH;
  }

  private getSpacerColumn(granularity: HistoryGranularity, gridShell?: HTMLElement | null): HistoryGridColumn {
    const width = Math.max(
      HISTORY_DATA_COLUMN_MIN_WIDTH,
      Math.floor(gridShell?.clientWidth ?? 0) - this.getGridContentWidth(granularity),
    );

    return {
      dataField: HISTORY_SPACER_COLUMN_FIELD,
      caption: '',
      dataType: 'string',
      minWidth: HISTORY_DATA_COLUMN_MIN_WIDTH,
      width,
      alignment: 'left',
      allowResizing: false,
      cssClass: HISTORY_SPACER_COLUMN_CLASS,
    };
  }

  private getGridContentWidth(granularity: HistoryGranularity): number {
    const hasComparisonColumns = this.hasComparisonColumns(granularity);

    return this.visibleYearColumnsSig().reduce((width, year) => {
      if (year === this.currentYear && hasComparisonColumns) {
        return width + HISTORY_DATA_COLUMN_MIN_WIDTH + COMPARISON_COLUMN_WIDTH * 2;
      }

      return width + HISTORY_DATA_COLUMN_MIN_WIDTH;
    }, 170);
  }

  private refreshGridLayout(): void {
    this.gridLayoutVersionSig.update(version => version + 1);
  }

  private async loadProductionHistory(scope: ReportingComparisonScope): Promise<void> {
    if (this.reportFilterReloadTimer) {
      clearTimeout(this.reportFilterReloadTimer);
      this.reportFilterReloadTimer = null;
    }

    const generation = ++this.loadGeneration;

    /**
     * Only a scope change hides the header. It waits there until the scope is known to be
     * servable, so a refusal never flashes its controls — but doing that on a filter or horizon
     * reload unmounts the control the viewer is using, dropdown and all, and the page looks stuck.
     */
    const isScopeChange = scope !== this.lastLoadedScope;
    this.lastLoadedScope = scope;
    if (isScopeChange) {
      this.hasLoadedSig.set(false);
      // A filter picked for one scope means nothing in another: its options are a different set.
      this.selectedReportFilterValuesSig.set([]);
    }

    this.isLoadingSig.set(true);
    this.loadErrorMessageSig.set(null);
    this.unavailableReasonSig.set(null);

    let monthlyItems: ProductionHistoryMonthlyItemResponse[] = [];
    let quarterlyItems: ProductionHistoryQuarterlyItemResponse[] = [];
    let yearlyItems: ProductionHistoryYearlyItemResponse[] = [];
    let lastTransactionDate: string | null = null;
    let meta: ProductionHistoryResponseMeta = {};
    let apiLoadError: unknown = null;

    try {
      const query = this.getHistoryQuery(scope);
      const [monthlyResponse, quarterlyResponse, yearlyResponse] = await Promise.all([
        this.productionHistoryService.getMonthlyProductionHistory(query),
        this.productionHistoryService.getQuarterlyProductionHistory(query),
        this.productionHistoryService.getYearlyProductionHistory(query),
      ]);

      monthlyItems = monthlyResponse.data;
      quarterlyItems = quarterlyResponse.data;
      yearlyItems = yearlyResponse.data;
      lastTransactionDate = this.getLatestMaxTransdate(yearlyResponse.data);
      meta = this.getResponseMeta(monthlyResponse, quarterlyResponse, yearlyResponse);
    } catch (error) {
      apiLoadError = error;
    }

    if (generation !== this.loadGeneration) {
      return;
    }

    // A refused viewer gets the reason only.
    const unavailableReason = getReportUnavailableReason(apiLoadError);
    if (unavailableReason) {
      this.historySourceSig.set(null);
      this.unavailableReasonSig.set(unavailableReason);
      this.isLoadingSig.set(false);
      return;
    }

    this.hasLoadedSig.set(true);

    if (apiLoadError) {
      console.error('[ProductionHistoryDashboard] Failed to load production history', apiLoadError);
    }

    this.historySourceSig.set({ monthlyItems, quarterlyItems, yearlyItems, lastTransactionDate, meta });

    const hasApiHistoryData = this.hasAnyHistoryData(
      this.buildApiHistoryDefaults(monthlyItems, quarterlyItems, yearlyItems, lastTransactionDate),
    );
    this.initializeYearSelectionIfNeeded(this.historyDataSig());

    if (apiLoadError) {
      this.loadErrorMessageSig.set(this.getLoadErrorMessage(apiLoadError));
    } else if (!hasApiHistoryData) {
      this.loadErrorMessageSig.set('Unable to load production history data.');
    }

    this.isLoadingSig.set(false);
  }

  private getHistoryQuery(scope: ReportingComparisonScope): ProductionHistoryHttpQuery {
    const query: ProductionHistoryHttpQuery = {
      scope,
      ...toReportFilters(this.selectedReportFilterValuesSig()),
      horizon: this.isProjectionHorizonSig() ? ProductionHorizon.Projection : ProductionHorizon.Actual,
    };

    if (this.isProjectionHorizonSig()) {
      query.method = this.selectedProjectionMethodSig();
    }

    return query;
  }

  /**
   * The three calls carry the same metadata; the yearly one is preferred only because it is the
   * grain the projection is anchored on, and any of them answering is enough.
   */
  private getResponseMeta(
    monthlyResponse: ProductionHistoryResponseMeta & { data: ProductionHistoryMonthlyItemResponse[] },
    quarterlyResponse: ProductionHistoryResponseMeta & { data: ProductionHistoryQuarterlyItemResponse[] },
    yearlyResponse: ProductionHistoryResponseMeta & { data: ProductionHistoryYearlyItemResponse[] },
  ): ProductionHistoryResponseMeta {
    return {
      horizon: yearlyResponse.horizon ?? quarterlyResponse.horizon ?? monthlyResponse.horizon,
      method: yearlyResponse.method ?? quarterlyResponse.method ?? monthlyResponse.method,
      projectionAsOfDate: this.getProjectionAsOfDate(monthlyResponse, quarterlyResponse, yearlyResponse),
      lagWeeks: yearlyResponse.lagWeeks ?? quarterlyResponse.lagWeeks ?? monthlyResponse.lagWeeks,
      asOfWeek: yearlyResponse.asOfWeek ?? quarterlyResponse.asOfWeek ?? monthlyResponse.asOfWeek,
      dataWeek: yearlyResponse.dataWeek ?? quarterlyResponse.dataWeek ?? monthlyResponse.dataWeek,
    };
  }

  private getProjectionAsOfDate(
    monthlyResponse: ProductionHistoryResponseMeta & { data: ProductionHistoryMonthlyItemResponse[] },
    quarterlyResponse: ProductionHistoryResponseMeta & { data: ProductionHistoryQuarterlyItemResponse[] },
    yearlyResponse: ProductionHistoryResponseMeta & { data: ProductionHistoryYearlyItemResponse[] },
  ): string | null {
    const responseDate =
      yearlyResponse.projectionAsOfDate ??
      quarterlyResponse.projectionAsOfDate ??
      monthlyResponse.projectionAsOfDate ??
      null;
    if (responseDate) {
      return responseDate;
    }

    // Older responses carry it per row instead of on the envelope.
    return (
      yearlyResponse.data.find(item => item.projectionAsOfDate)?.projectionAsOfDate ??
      quarterlyResponse.data.find(item => item.projectionAsOfDate)?.projectionAsOfDate ??
      monthlyResponse.data.find(item => item.projectionAsOfDate)?.projectionAsOfDate ??
      null
    );
  }

  private buildApiHistoryDefaults(
    monthlyItems: ProductionHistoryMonthlyItemResponse[],
    quarterlyItems: ProductionHistoryQuarterlyItemResponse[],
    yearlyItems: ProductionHistoryYearlyItemResponse[],
    lastTransactionDate: string | null,
  ): ProductionHistoryDashboardDefaults {
    // Once the API projects for itself its rows are authoritative and the client-side fill stands down.
    const useApiProjection = this.usesApiProjectionContract(monthlyItems, quarterlyItems, yearlyItems);

    return {
      data: {
        [Granularity.Monthly]: this.mapMonthlyRows(monthlyItems, yearlyItems, useApiProjection),
        [Granularity.Quarterly]: this.mapQuarterlyRows(quarterlyItems, monthlyItems, yearlyItems, useApiProjection),
        [Granularity.Annual]: this.mapAnnualRows(monthlyItems, yearlyItems, lastTransactionDate, useApiProjection),
      },
      summaryData: {
        [Granularity.Monthly]: this.mapMonthlyAverageSummary(monthlyItems),
      },
    };
  }

  /** The API rows in display order, with the ranking, comparison and projection decoration the grids use. */
  private shapeHistoryDefaults(
    apiDefaults: ProductionHistoryDashboardDefaults,
    lastTransactionDate: string | null,
  ): ProductionHistoryDashboardDefaults {
    const monthlyRows = this.withProjectionHighlighting(
      this.withCurrentYearComparisonColumns(
        this.withMonthlyHighlightMetadata(this.arrangeRowsByLabel(apiDefaults.data[Granularity.Monthly], MONTH_LABELS)),
        Granularity.Monthly,
      ),
      Granularity.Monthly,
    );
    const quarterlyRows = this.withProjectionHighlighting(
      this.withCurrentYearComparisonColumns(
        this.withQuarterlyHighlightMetadata(
          this.arrangeRowsByLabel(
            apiDefaults.data[Granularity.Quarterly],
            [1, 2, 3, 4].map(quarter => this.getQuarterLabel(quarter)),
          ),
        ),
        Granularity.Quarterly,
      ),
      Granularity.Quarterly,
    );
    const annualRows = this.withProjectionHighlighting(
      this.buildAnnualRows(apiDefaults.data[Granularity.Annual], lastTransactionDate),
      Granularity.Annual,
    );

    return {
      data: {
        [Granularity.Monthly]: monthlyRows,
        [Granularity.Quarterly]: quarterlyRows,
        [Granularity.Annual]: annualRows,
      },
      summaryData: {
        [Granularity.Monthly]: this.getDisplayedMonthlySummary(apiDefaults),
      },
    };
  }

  /**
   * The AVERAGE row belongs to Actual Production only (TASKID-3398).
   *
   * Against a projection it said nothing — an average of months that are themselves projected is
   * just the projection again, divided by twelve. Against actual production it answers a real
   * question: what has an average month of this year produced so far. It shows the years the
   * monthly rows have.
   */
  private getDisplayedMonthlySummary(
    apiDefaults: ProductionHistoryDashboardDefaults,
  ): ProductionHistoryGridSummary | null {
    const apiSummary = apiDefaults.summaryData[Granularity.Monthly] ?? null;
    if (!this.isActualHorizonSig() || !apiSummary) {
      return null;
    }

    const monthlyYears = new Set(this.getPresentYears(apiDefaults.data[Granularity.Monthly]));
    const summary: ProductionHistoryGridSummary = {
      label: apiSummary.label ?? PRODUCTION_HISTORY_AVERAGE_LABEL,
      priorPeriodChange: apiSummary.priorPeriodChange ?? null,
      currentYearChange: apiSummary.currentYearChange ?? null,
    };

    this.getPresentYears([apiSummary])
      .filter(year => monthlyYears.has(year))
      .forEach(year => {
        const field = this.getYearField(year);
        summary[field] = apiSummary[field] ?? null;
      });

    return summary;
  }

  /** One row per label, in the order given; labels with no row are left out. */
  private arrangeRowsByLabel(
    rows: ProductionHistoryDashboardGridRow[],
    labels: string[],
  ): ProductionHistoryDashboardGridRow[] {
    const rowsByLabel = new Map(rows.map(row => [this.getLabelKey(row.label), row]));

    return labels
      .map(label => {
        const row = rowsByLabel.get(this.getLabelKey(label));
        return row ? this.copyYearValues(row, label) : null;
      })
      .filter((row): row is ProductionHistoryDashboardGridRow => !!row);
  }

  private buildAnnualRows(
    apiRows: ProductionHistoryDashboardGridRow[],
    lastTransactionDate: string | null,
  ): ProductionHistoryDashboardGridRow[] {
    const apiProductionRow = this.getAnnualProductionRow(apiRows);
    if (!apiProductionRow) {
      return [];
    }

    const productionRowLabel = lastTransactionDate
      ? this.getAnnualProductionRowLabel(lastTransactionDate)
      : (apiProductionRow.label ?? this.getAnnualProductionRowLabel(null));
    const productionRow = this.copyYearValues(apiProductionRow, productionRowLabel);
    productionRow.annualRowKind = 'production';
    productionRow.valueKind = 'currency';

    return [productionRow, this.createAnnualPriorYearChangeRow(productionRow)];
  }

  /** A copy of the row's year values and their projection metadata, under the given label. */
  private copyYearValues(source: ProductionHistoryDashboardGridRow, label: string): ProductionHistoryDashboardGridRow {
    const row: ProductionHistoryDashboardGridRow = {
      label,
      priorPeriodChange: source.priorPeriodChange ?? null,
      currentYearChange: source.currentYearChange ?? null,
    };

    this.getPresentYears([source]).forEach(year => {
      row[this.getYearField(year)] = this.getNumericRowValue(source, this.getYearField(year));
      this.copyProjectionYearMetadata(row, source, year);
    });

    return row;
  }

  /** Quarterly labels have carried a trailing asterisk, so labels are matched without it. */
  private getLabelKey(label: string): string {
    return label
      .replace(/\s*\*+$/, '')
      .trim()
      .toLowerCase();
  }

  private getAnnualProductionRow(rows: ProductionHistoryDashboardGridRow[]): ProductionHistoryDashboardGridRow | null {
    return (
      rows.find(row => row.annualRowKind === 'production') ??
      rows.find(row => row.valueKind !== 'percent') ??
      rows[0] ??
      null
    );
  }

  private hasAnyHistoryData(defaults: ProductionHistoryDashboardDefaults): boolean {
    return Object.values(defaults.data).some(rows => rows.some(row => this.getPresentYears([row]).length > 0));
  }

  private withMonthlyHighlightMetadata(rows: ProductionHistoryDashboardGridRow[]): ProductionHistoryDashboardGridRow[] {
    const rankByLabel = this.getRanksByLabel(rows);
    const globalTopPremium = this.getGlobalTopPremium(rows);

    return rows.map(row => ({
      ...row,
      monthlyRankByYear: rankByLabel[row.label] ?? {},
      globalTopMonthByYear: this.getGlobalTopByYear(row, globalTopPremium),
    }));
  }

  private withQuarterlyHighlightMetadata(
    rows: ProductionHistoryDashboardGridRow[],
  ): ProductionHistoryDashboardGridRow[] {
    const rankByLabel = this.getRanksByLabel(rows);
    const globalTopPremium = this.getGlobalTopPremium(rows);

    return rows.map(row => ({
      ...row,
      quarterlyRankByYear: rankByLabel[row.label] ?? {},
      globalTopQuarterByYear: this.getGlobalTopByYear(row, globalTopPremium),
    }));
  }

  private getRanksByLabel(rows: ProductionHistoryDashboardGridRow[]): Record<string, Record<number, number>> {
    const years = this.getPresentYears(rows);
    const ranksByLabel: Record<string, Record<number, number>> = {};

    years.forEach(year => {
      const field = this.getYearField(year);
      // Only production ranks: a $0.00 month (TASKID-3477 fills quiet months with zero) is no
      // one's best month, and a year of zeros would otherwise paint its first three green.
      const rankedRows = rows
        .map(row => ({ label: row.label, value: this.getNumericRowValue(row, field) }))
        .filter(item => item.value !== null && item.value > 0)
        .sort((left, right) => Number(right.value) - Number(left.value));

      rankedRows.forEach((item, index) => {
        ranksByLabel[item.label] ??= {};
        ranksByLabel[item.label][year] = index + 1;
      });
    });

    return ranksByLabel;
  }

  private getGlobalTopPremium(rows: ProductionHistoryDashboardGridRow[]): number | null {
    return rows.reduce<number | null>((topPremium, row) => {
      this.getPresentYears([row]).forEach(year => {
        const value = this.getNumericRowValue(row, this.getYearField(year));

        if (value === null || value <= 0) {
          return;
        }

        if (topPremium === null || value > topPremium) {
          topPremium = value;
        }
      });

      return topPremium;
    }, null);
  }

  private getGlobalTopByYear(
    row: ProductionHistoryDashboardGridRow,
    globalTopPremium: number | null,
  ): Record<number, boolean> {
    return Object.fromEntries(
      this.getPresentYears([row]).map(year => {
        const value = this.getNumericRowValue(row, this.getYearField(year));
        return [year, globalTopPremium !== null && value === globalTopPremium];
      }),
    );
  }

  private getNumericRowValue(row: ProductionHistoryDashboardGridRow | null | undefined, field: string): number | null {
    const value = row?.[field];
    return typeof value === 'number' ? value : null;
  }

  private getPresentYears(rows: ProductionHistoryDashboardGridRow[]): number[] {
    const years = new Set<number>();

    rows.forEach(row => {
      Object.keys(row).forEach(key => {
        const match = key.match(/^year_(\d+)$/);
        if (!match || this.getNumericRowValue(row, key) === null) {
          return;
        }

        years.add(Number(match[1]));
      });
    });

    return Array.from(years).sort((a, b) => b - a);
  }

  private mapMonthlyRows(
    items: ProductionHistoryMonthlyItemResponse[],
    yearlyItems: ProductionHistoryYearlyItemResponse[],
    useApiProjection: boolean,
  ): ProductionHistoryDashboardGridRow[] {
    const globalTopPremium = items.reduce<number | null>((topPremium, item) => {
      if (topPremium === null || item.premium > topPremium) {
        return item.premium;
      }

      return topPremium;
    }, null);
    const currentYearSeries = useApiProjection
      ? null
      : getCurrentYearMonthSeries(items, yearlyItems, this.getHorizonContext());
    const currentYearField = this.getYearField(this.currentYear);

    return Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;
      const periodItems = items.filter(item => item.month === month);
      const latestItem = this.getLatestYearItem(periodItems);
      const row: ProductionHistoryDashboardGridRow = {
        ...this.createGridRow(
          MONTH_LABELS[index],
          periodItems,
          this.normalizeApiPercentValue(latestItem?.changeOverMonth),
          this.normalizeApiPercentValue(latestItem?.changeOverYear),
        ),
        monthlyRankByYear: Object.fromEntries(periodItems.map(item => [item.year, item.monthRankInYear])),
        globalTopMonthByYear: Object.fromEntries(
          periodItems.map(item => [item.year, globalTopPremium !== null && item.premium === globalTopPremium]),
        ),
        ...this.getProjectionYearMetadata(periodItems),
      };

      if (useApiProjection) {
        return row;
      }

      const currentYearValue = currentYearSeries?.[index] ?? null;
      if (currentYearValue === null) {
        delete row[currentYearField];
        return row;
      }

      row[currentYearField] = currentYearValue;
      // A month with no paid row of its own but a value in the series was filled by the projection.
      if (!periodItems.some(item => item.year === this.currentYear)) {
        row.isProjectedByYear = { ...row.isProjectedByYear, [this.currentYear]: true };
        row.premiumSourceByYear = { ...row.premiumSourceByYear, [this.currentYear]: 'projected' };
      }

      return row;
    });
  }

  private mapQuarterlyRows(
    items: ProductionHistoryQuarterlyItemResponse[],
    monthlyItems: ProductionHistoryMonthlyItemResponse[],
    yearlyItems: ProductionHistoryYearlyItemResponse[],
    useApiProjection: boolean,
  ): ProductionHistoryDashboardGridRow[] {
    const globalTopPremium = items.reduce<number | null>((topPremium, item) => {
      if (topPremium === null || item.premium > topPremium) {
        return item.premium;
      }

      return topPremium;
    }, null);
    // Quarters are summed from the month series so a quarter can never disagree with its months.
    const currentYearSeries = useApiProjection
      ? null
      : getCurrentYearMonthSeries(monthlyItems, yearlyItems, this.getHorizonContext());
    const currentYearField = this.getYearField(this.currentYear);

    return Array.from({ length: 4 }, (_, index) => {
      const quarter = index + 1;
      const periodItems = items.filter(item => item.quarter === quarter);
      const latestItem = this.getLatestYearItem(periodItems);
      const row: ProductionHistoryDashboardGridRow = {
        ...this.createGridRow(
          this.getQuarterLabel(quarter),
          periodItems,
          this.normalizeApiPercentValue(latestItem?.changeOverQuarter),
          this.normalizeApiPercentValue(latestItem?.changeOverYear),
        ),
        quarterlyRankByYear: Object.fromEntries(periodItems.map(item => [item.year, item.quarterRankInYear])),
        globalTopQuarterByYear: Object.fromEntries(
          periodItems.map(item => [item.year, globalTopPremium !== null && item.premium === globalTopPremium]),
        ),
        ...this.getProjectionYearMetadata(periodItems),
      };

      if (useApiProjection) {
        return row;
      }

      const currentYearValue = getCurrentYearQuarterValue(
        currentYearSeries ?? [],
        quarter,
        this.getHorizonContext().horizon,
      );

      if (currentYearValue === null) {
        delete row[currentYearField];
        return row;
      }

      row[currentYearField] = currentYearValue;
      if (!periodItems.some(item => item.year === this.currentYear) && this.isProjectionHorizonSig()) {
        row.isProjectedByYear = { ...row.isProjectedByYear, [this.currentYear]: true };
        row.premiumSourceByYear = { ...row.premiumSourceByYear, [this.currentYear]: 'projected' };
      }

      return row;
    });
  }

  private mapAnnualRows(
    monthlyItems: ProductionHistoryMonthlyItemResponse[],
    yearlyItems: ProductionHistoryYearlyItemResponse[],
    lastTransactionDate: string | null,
    useApiProjection: boolean,
  ): ProductionHistoryDashboardGridRow[] {
    const productionRow = this.createAnnualProductionRow(
      monthlyItems,
      yearlyItems,
      lastTransactionDate,
      useApiProjection,
    );
    if (!productionRow) {
      return [];
    }

    return [productionRow, this.createAnnualPriorYearChangeRow(productionRow)];
  }

  private mapMonthlyAverageSummary(items: ProductionHistoryMonthlyItemResponse[]): ProductionHistoryGridSummary | null {
    if (!items.length || !this.isActualHorizonSig()) {
      return null;
    }

    const latestItemsByYear = new Map<number, ProductionHistoryMonthlyItemResponse>();
    items.forEach(item => {
      const current = latestItemsByYear.get(item.year);
      if (!current || item.month > current.month) {
        latestItemsByYear.set(item.year, item);
      }
    });

    /**
     * The current year is measured over elapsed DAYS, not over the months on file: `avgPerYear`
     * divides by whole months, which counts a part-finished month as a full one and understates
     * the average for as long as that month is running. A finished year has neither problem, so
     * prior years keep the figure the API already sends.
     */
    const currentYearRate = getAverageMonthlyProductionRate(items, this.getHorizonContext());

    return {
      label: PRODUCTION_HISTORY_AVERAGE_LABEL,
      priorPeriodChange: null,
      currentYearChange: null,
      ...Object.fromEntries(
        Array.from(latestItemsByYear.values()).map(item => [
          this.getYearField(item.year),
          item.year === this.currentYear && currentYearRate !== null ? currentYearRate : item.avgPerYear,
        ]),
      ),
    };
  }

  private createGridRow<T extends { year: number; premium: number }>(
    label: string,
    items: T[],
    priorPeriodChange: number | null,
    currentYearChange: number | null,
  ): ProductionHistoryDashboardGridRow {
    return {
      label,
      priorPeriodChange,
      currentYearChange,
      ...Object.fromEntries(items.map(item => [this.getYearField(item.year), item.premium])),
    };
  }

  private createAnnualProductionRow(
    monthlyItems: ProductionHistoryMonthlyItemResponse[],
    yearlyItems: ProductionHistoryYearlyItemResponse[],
    lastTransactionDate: string | null,
    useApiProjection: boolean,
  ): ProductionHistoryDashboardGridRow | null {
    /**
     * The years come from the data, never from the grids' own visible columns. Those are derived
     * from this function, so asking for them here would make the computed read itself.
     */
    const annualYears = this.getHistoryYearRangeFromYears([
      ...monthlyItems.map(item => item.year),
      ...yearlyItems.map(item => item.year),
    ]);
    const yearlyItemsByYear = new Map(yearlyItems.map(item => [item.year, item]));
    const currentYearSeries = useApiProjection
      ? this.getApiCurrentYearMonthSeries(monthlyItems)
      : getCurrentYearMonthSeries(monthlyItems, yearlyItems, this.getHorizonContext());
    const row: ProductionHistoryDashboardGridRow = {
      label: this.getAnnualProductionRowLabel(lastTransactionDate),
      priorPeriodChange: null,
      currentYearChange: null,
      annualRowKind: 'production',
      valueKind: 'currency',
      ...this.getProjectionYearMetadata(yearlyItems),
    };
    let hasValue = false;

    annualYears.forEach(year => {
      const annualValue = this.getAnnualProductionValue(
        year,
        monthlyItems,
        yearlyItemsByYear.get(year),
        currentYearSeries,
      );
      if (annualValue === null) {
        return;
      }

      row[this.getYearField(year)] = annualValue;
      hasValue = true;
    });

    if (!useApiProjection && this.isProjectionHorizonSig()) {
      row.isProjectedByYear = { ...row.isProjectedByYear, [this.currentYear]: true };
      row.premiumSourceByYear = { ...row.premiumSourceByYear, [this.currentYear]: 'projected' };
    }

    return hasValue ? row : null;
  }

  private createAnnualPriorYearChangeRow(
    productionRow: ProductionHistoryDashboardGridRow,
  ): ProductionHistoryDashboardGridRow {
    const changeRow: ProductionHistoryDashboardGridRow = {
      label: '% change over the prior year',
      priorPeriodChange: null,
      currentYearChange: null,
      annualRowKind: 'priorYearChange',
      valueKind: 'percent',
    };

    this.getHistoryYearRangeFromYears(this.getPresentYears([productionRow])).forEach(year => {
      const currentValue = this.getNumericRowValue(productionRow, this.getYearField(year));
      const previousValue = this.getNumericRowValue(productionRow, this.getYearField(year - 1));

      if (currentValue === null || previousValue === null) {
        return;
      }

      const change = this.getPercentChange(currentValue, previousValue);
      changeRow[this.getYearField(year)] = change;
      if (change === null) {
        return;
      }

      // A change against a projected figure is itself a projection, and reads as one.
      if (this.isProjectedYearValue(productionRow, year) || this.isProjectedYearValue(productionRow, year - 1)) {
        changeRow.isProjectionDerivedByYear = { ...changeRow.isProjectionDerivedByYear, [year]: true };
      }
    });

    return changeRow;
  }

  /**
   * A completed year is whatever the API says it was. Only the running year is open to the
   * horizon, and there the answer is the month series — actual months as they stand, plus the
   * projected fill when the Projection horizon is on — so the annual figure always equals the
   * sum of the months shown above it.
   */
  private getAnnualProductionValue(
    year: number,
    monthlyItems: ProductionHistoryMonthlyItemResponse[],
    yearlyItem: ProductionHistoryYearlyItemResponse | undefined,
    currentYearSeries: Array<number | null>,
  ): number | null {
    if (yearlyItem) {
      return yearlyItem.premium;
    }

    if (year !== this.currentYear) {
      return this.getHistoricalAnnualProduction(year, monthlyItems);
    }

    return getCurrentYearAnnualValue(currentYearSeries);
  }

  private getHistoricalAnnualProduction(
    year: number,
    monthlyItems: ProductionHistoryMonthlyItemResponse[],
  ): number | null {
    const yearMonthlyItems = monthlyItems.filter(item => item.year === year);

    if (!yearMonthlyItems.length) {
      return null;
    }

    return yearMonthlyItems.reduce((sum, item) => sum + item.premium, 0);
  }

  /** The API's own current-year months, used verbatim once it sends projection rows of its own. */
  private getApiCurrentYearMonthSeries(monthlyItems: ProductionHistoryMonthlyItemResponse[]): Array<number | null> {
    const series: Array<number | null> = Array.from({ length: 12 }, () => null);

    monthlyItems.forEach(item => {
      if (item.year === this.currentYear) {
        series[item.month - 1] = item.premium;
      }
    });

    return series;
  }

  private getPercentChange(currentValue: number | null, previousValue: number | null): number | null {
    if (currentValue === null || previousValue === null || previousValue === 0) {
      return null;
    }

    return (currentValue - previousValue) / previousValue;
  }

  private getAnnualProductionRowLabel(lastTransactionDate: string | Date | null): string {
    return `Year to date (${this.formatAnnualReportDate(lastTransactionDate)})`;
  }

  private formatAnnualReportDate(lastTransactionDate: string | Date | null): string {
    const date = this.parseAnnualReportDate(lastTransactionDate);
    if (!date) {
      return 'latest upload';
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  }

  private parseAnnualReportDate(value: string | Date | null): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const isoDateMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDateMatch) {
      return new Date(Number(isoDateMatch[1]), Number(isoDateMatch[2]) - 1, Number(isoDateMatch[3]));
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private getLatestYearItem<T extends { year: number }>(items: T[]): T | null {
    return items.reduce<T | null>((latestItem, item) => {
      if (!latestItem || item.year > latestItem.year) {
        return item;
      }

      return latestItem;
    }, null);
  }

  private getLatestMaxTransdate(yearlyItems: ProductionHistoryYearlyItemResponse[]): string | null {
    return yearlyItems.reduce<string | null>((latestDate, item) => {
      const lastTransactionDate = item.maxTransdate?.trim();
      if (!lastTransactionDate) {
        return latestDate;
      }

      if (!latestDate || lastTransactionDate > latestDate) {
        return lastTransactionDate;
      }

      return latestDate;
    }, null);
  }

  private normalizeApiPercentValue(value: number | null | undefined): number | null {
    return value === null || value === undefined ? null : value / 100;
  }

  private getAvailableYears(historyData: ProductionHistoryDashboardData): number[] {
    const years = new Set<number>();

    Object.values(historyData).forEach(rows => {
      rows.forEach(row => {
        Object.keys(row).forEach(key => {
          const match = key.match(/^year_(\d+)$/);
          if (match) {
            years.add(Number(match[1]));
          }
        });
      });
    });

    return Array.from(years).sort((a, b) => b - a);
  }

  private getHistoryYearRange(historyData: ProductionHistoryDashboardData): number[] {
    return this.getHistoryYearRangeFromYears(this.getAvailableYears(historyData));
  }

  /** Every year from the latest to the earliest the data holds; the running year always has a column. */
  private getHistoryYearRangeFromYears(availableYears: number[]): number[] {
    const latestYear = Math.max(this.currentYear, ...availableYears);
    const earliestYear = Math.min(this.currentYear, ...availableYears);

    return Array.from({ length: latestYear - earliestYear + 1 }, (_, index) => latestYear - index);
  }

  private getQuarterLabel(quarter: number): string {
    return getQuarterLabel(quarter);
  }

  private getMonthlyProductionCellRank(cellInfo: ProductionHistoryGridCellInfo): number | null {
    const year = this.getCellYear(cellInfo);
    if (!year) {
      return null;
    }

    return cellInfo.data?.monthlyRankByYear?.[year] ?? null;
  }

  private getQuarterlyProductionCellRank(cellInfo: ProductionHistoryGridCellInfo): number | null {
    const year = this.getCellYear(cellInfo);
    if (!year) {
      return null;
    }

    return cellInfo.data?.quarterlyRankByYear?.[year] ?? null;
  }

  private getCellYear(cellInfo: ProductionHistoryGridCellInfo): number | null {
    const match = cellInfo.column?.dataField?.match(/^year_(\d+)$/);
    if (!match) {
      return null;
    }

    return Number(match[1]);
  }

  private getLoadErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Unable to load production history data.';
  }

  private getExportSections(): ProductionHistoryExportSection[] {
    return this.historySections.map(section => ({
      granularity: section.id,
      title: this.getSectionTitle(section.id),
      columns: this.getExportColumns(section.id),
      rows: this.getGridRows(section.id),
      summaryRow: this.getGridSummary(section.id),
      footnote: this.getTableFootnote(section.id),
    }));
  }

  private getExportColumns(granularity: HistoryGranularity): ProductionHistoryExportColumn[] {
    const hasComparisonColumns = this.hasComparisonColumns(granularity);
    const currentYearGroupHeader = `${this.currentYear}`;
    const yearColumns = this.visibleYearColumnsSig().flatMap(year => {
      const productionColumn: ProductionHistoryExportColumn = {
        key: this.getYearField(year),
        header: year === this.currentYear && hasComparisonColumns ? 'Production' : `${year}`,
        kind: granularity === Granularity.Annual ? 'annual' : 'currency',
        year,
        ...(year === this.currentYear && hasComparisonColumns ? { groupHeader: currentYearGroupHeader } : {}),
      };

      if (year !== this.currentYear || !hasComparisonColumns) {
        return [productionColumn];
      }

      return [
        {
          key: 'priorPeriodChange',
          header: this.getPriorPeriodChangeCaption(granularity).replace(/\u00A0/g, ' '),
          kind: 'percent' as const,
          groupHeader: currentYearGroupHeader,
        },
        {
          key: 'currentYearChange',
          header: '% change for the current year over last year',
          kind: 'percent' as const,
          groupHeader: currentYearGroupHeader,
        },
        productionColumn,
      ];
    });

    return [
      {
        key: 'label',
        header: this.getPeriodColumnCaption(granularity),
        kind: 'text',
      },
      ...yearColumns,
    ];
  }

  private buildExportFileName(): string {
    const date = new Date();
    const datePart = [
      date.getFullYear(),
      `${date.getMonth() + 1}`.padStart(2, '0'),
      `${date.getDate()}`.padStart(2, '0'),
    ].join('-');

    // Named after the title, so a filtered or MGA export does not download as "alliance-group".
    const titlePart = this.pageTitleSig()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return `${titlePart}-${datePart}`;
  }

  /**
   * The monthly and quarterly grids compare the running year against the period before it and
   * against the same period last year. Both are recomputed here rather than taken from the API,
   * because under a projection horizon the figures they compare have changed.
   */
  private withCurrentYearComparisonColumns(
    rows: ProductionHistoryDashboardGridRow[],
    granularity: HistoryGranularity,
  ): ProductionHistoryDashboardGridRow[] {
    if (granularity === Granularity.Annual) {
      return rows;
    }

    return rows.map((row, index) => {
      const currentValue = this.getNumericRowValue(row, this.getYearField(this.currentYear));
      const priorYearValue = this.getNumericRowValue(row, this.getYearField(this.currentYear - 1));
      // January's previous period is last December, which lives in the prior year's column.
      const previousPeriodYear = index ? this.currentYear : this.currentYear - 1;
      const previousPeriodRow = index ? rows[index - 1] : rows[rows.length - 1];

      return {
        ...row,
        priorPeriodChange: this.getPercentChange(currentValue, this.getPreviousPeriodValue(rows, index)),
        currentYearChange: this.getPercentChange(currentValue, priorYearValue),
        priorPeriodChangeIsProjectionDerived:
          this.isProjectionHorizonSig() &&
          (this.isProjectedYearValue(row, this.currentYear) ||
            this.isProjectedYearValue(previousPeriodRow, previousPeriodYear)),
        currentYearChangeIsProjectionDerived:
          this.isProjectionHorizonSig() &&
          (this.isProjectedYearValue(row, this.currentYear) || this.isProjectedYearValue(row, this.currentYear - 1)),
      };
    });
  }

  private getPreviousPeriodValue(rows: ProductionHistoryDashboardGridRow[], index: number): number | null {
    if (!index) {
      return this.getNumericRowValue(rows[rows.length - 1], this.getYearField(this.currentYear - 1));
    }

    return this.getNumericRowValue(rows[index - 1], this.getYearField(this.currentYear));
  }

  /**
   * Marks the cells the footnote is talking about.
   *
   * Under the Actual horizon nothing is projected, so every mark is cleared rather than left
   * over from the previous horizon.
   */
  private withProjectionHighlighting(
    rows: ProductionHistoryDashboardGridRow[],
    granularity: HistoryGranularity,
  ): ProductionHistoryDashboardGridRow[] {
    if (this.isActualHorizonSig()) {
      return rows.map(row => ({
        ...row,
        isProjectionHighlightedByYear: {},
        isProjectionDerivedByYear: {},
        priorPeriodChangeIsProjectionDerived: false,
        currentYearChangeIsProjectionDerived: false,
      }));
    }

    const source = this.historySourceSig();
    const trustApiProjectionFlags = this.usesApiProjectionContract(
      source?.monthlyItems ?? [],
      source?.quarterlyItems ?? [],
      source?.yearlyItems ?? [],
    );

    return rows.map(row => {
      const isProjectionHighlightedByYear: Record<number, boolean> = { ...(row.isProjectionHighlightedByYear ?? {}) };

      this.getPresentYears([row]).forEach(year => {
        if (year < this.currentYear) {
          isProjectionHighlightedByYear[year] = false;
          return;
        }

        if (this.isProjectedYearValue(row, year)) {
          isProjectionHighlightedByYear[year] = true;
          return;
        }

        // Without the API's own flags, position is what tells a projected period from a paid one.
        if (!trustApiProjectionFlags && this.shouldHighlightCurrentOrFuturePeriod(row, year, granularity)) {
          isProjectionHighlightedByYear[year] = true;
        }
      });

      if (row.annualRowKind === 'priorYearChange' && row.isProjectionDerivedByYear?.[this.currentYear]) {
        isProjectionHighlightedByYear[this.currentYear] = true;
      }

      const isCurrentYearHighlighted = isProjectionHighlightedByYear[this.currentYear] === true;

      return {
        ...row,
        isProjectionHighlightedByYear,
        priorPeriodChangeIsProjectionDerived:
          row.priorPeriodChangeIsProjectionDerived === true || isCurrentYearHighlighted,
        currentYearChangeIsProjectionDerived:
          row.currentYearChangeIsProjectionDerived === true || isCurrentYearHighlighted,
      };
    });
  }

  /** A period at or past the trusted paid-through point is filled by the projection, not by the file. */
  private shouldHighlightCurrentOrFuturePeriod(
    row: ProductionHistoryDashboardGridRow,
    year: number,
    granularity: HistoryGranularity,
  ): boolean {
    if (year < this.currentYear) {
      return false;
    }

    const through = this.getTrustedPaidThroughParts();
    if (year !== through.year) {
      return year > through.year;
    }

    if (granularity === Granularity.Monthly) {
      const month = this.getRowMonth(row);
      return month !== null && month >= through.month;
    }

    if (granularity === Granularity.Quarterly) {
      const quarter = this.getRowQuarter(row);
      return quarter !== null && quarter >= Math.ceil(through.month / 3);
    }

    return true;
  }

  private getRowMonth(row: ProductionHistoryDashboardGridRow): number | null {
    const labelKey = this.getLabelKey(row.label);
    const monthIndex = MONTH_LABELS.findIndex(label => label.toLowerCase() === labelKey);

    return monthIndex < 0 ? null : monthIndex + 1;
  }

  private getRowQuarter(row: ProductionHistoryDashboardGridRow): number | null {
    return getQuarterNumberFromLabel(row.label);
  }

  /**
   * Carries the API's per-row projection flags onto the grid row, by year.
   *
   * Anything before the running year is stated as actual outright: a closed year is settled
   * production whatever the response says, and leaving it unset would let a stale flag tint it.
   */
  private getProjectionYearMetadata(
    items: Array<ProductionHistoryProjectionFields & { year: number }>,
  ): Pick<ProductionHistoryDashboardGridRow, 'isProjectedByYear' | 'premiumSourceByYear'> {
    const isProjectedByYear: Record<number, boolean> = {};
    const premiumSourceByYear: Record<number, ProductionHistoryPremiumSource> = {};

    items.forEach(item => {
      if (item.year < this.currentYear) {
        isProjectedByYear[item.year] = false;
        premiumSourceByYear[item.year] = 'actual';
        return;
      }

      if (item.isProjected !== undefined) {
        isProjectedByYear[item.year] = item.isProjected;
      }

      if (item.premiumSource) {
        premiumSourceByYear[item.year] = item.premiumSource;
      }
    });

    return { isProjectedByYear, premiumSourceByYear };
  }

  private copyProjectionYearMetadata(
    target: ProductionHistoryDashboardGridRow,
    source: ProductionHistoryDashboardGridRow,
    year: number,
  ): void {
    if (year < this.currentYear) {
      target.isProjectedByYear = { ...target.isProjectedByYear, [year]: false };
      target.premiumSourceByYear = { ...target.premiumSourceByYear, [year]: 'actual' };
      return;
    }

    if (this.getNumericRowValue(source, this.getYearField(year)) === null) {
      return;
    }

    if (source.isProjectedByYear?.[year] !== undefined) {
      target.isProjectedByYear = { ...target.isProjectedByYear, [year]: source.isProjectedByYear[year] };
    }

    if (source.premiumSourceByYear?.[year]) {
      target.premiumSourceByYear = { ...target.premiumSourceByYear, [year]: source.premiumSourceByYear[year] };
    }
  }

  private isProjectedYearValue(row: ProductionHistoryDashboardGridRow | undefined, year: number): boolean {
    if (!row || year < this.currentYear) {
      return false;
    }

    if (row.isProjectedByYear?.[year] === true) {
      return true;
    }

    const source = row.premiumSourceByYear?.[year];

    return source === 'projected' || source === 'mixed';
  }

  private getHorizonContext(): HistoryHorizonContext {
    const through = this.getTrustedPaidThroughParts();

    return {
      currentYear: this.currentYear,
      horizon: this.isProjectionHorizonSig() ? ProductionHorizon.Projection : ProductionHorizon.Actual,
      model: PROJECTION_MODEL_BY_METHOD[this.selectedProjectionMethodSig()],
      // A trusted date in an earlier year means this year has nothing settled to project from.
      trustedAsOfMonth: through.year === this.currentYear ? through.month : 0,
      trustedAsOfDate: new Date(through.year, through.month - 1, through.day),
      paidThroughDate: this.getPaidThroughDate(),
    };
  }

  /**
   * The last transaction on file, with no reporting lag (TASKID-3398).
   *
   * Run rate and the AVERAGE row divide by elapsed days, so they must count up to real data:
   * rolling the date back three weeks while keeping the full year-to-date over it inflates the
   * daily rate. The seasonal methods still use the lagged date, which is what it is for.
   */
  private getPaidThroughDate(): Date | null {
    const source = this.historySourceSig();
    const isoDate =
      this.toPlainIsoDate(source?.lastTransactionDate) ??
      this.toPlainIsoDate(this.paidFileAsOfDateSig()) ??
      this.getLatestPaidDataIsoDate();
    const parts = isoDate ? parseIsoDateParts(isoDate) : null;

    return parts ? new Date(parts.year, parts.month - 1, parts.day) : null;
  }

  /**
   * How far the paid data is trusted, which is not how far it runs.
   *
   * The API answers this itself once it projects. Otherwise the boundary is rolled back by the
   * same reporting lag Revenue Analytics uses, because carriers report late and the most recent
   * weeks on file are still filling in — projecting from them reads the year as slowing down.
   */
  private getTrustedPaidThroughIsoDate(): string {
    const source = this.historySourceSig();
    const apiThroughDate = this.toPlainIsoDate(source?.meta.projectionAsOfDate);
    if (apiThroughDate) {
      return apiThroughDate;
    }

    const dataBoundaryIso =
      this.toPlainIsoDate(source?.lastTransactionDate) ??
      this.toPlainIsoDate(this.paidFileAsOfDateSig()) ??
      this.getLatestPaidDataIsoDate() ??
      getLastPassedFriday();

    // Run rate is quoted through the last transaction on file (TASKID-3398), so it takes no lag —
    // and the footnote has to name the same date the figures were actually built from.
    if (PROJECTION_MODEL_BY_METHOD[this.selectedProjectionMethodSig()] === ProductionProjectionModel.RunRate) {
      return dataBoundaryIso;
    }

    return applyRevenueAnalyticsReportingLag(
      dataBoundaryIso,
      source?.meta.lagWeeks ?? PRODUCTION_HISTORY_REPORTING_LAG_WEEKS,
    );
  }

  /**
   * A calendar date the grids state, not an instant.
   *
   * `YYYY-MM-DD` is taken as written. Routing it through `Date` instead would read it as UTC
   * midnight and then format it locally, which moves it back a day everywhere west of Greenwich —
   * and this date is printed next to the year-to-date label, which parses the same string directly.
   */
  private toPlainIsoDate(value: Date | string | null | undefined): string | null {
    if (typeof value === 'string') {
      const isoDateMatch = value.trim().match(/^\d{4}-\d{2}-\d{2}/);

      return isoDateMatch ? isoDateMatch[0] : normalizeAsOfDate(value);
    }

    return normalizeAsOfDate(value);
  }

  private getTrustedPaidThroughParts(): ProductionHistoryDateParts {
    return (
      parseIsoDateParts(this.getTrustedPaidThroughIsoDate()) ?? {
        year: this.currentYear,
        month: new Date().getMonth() + 1,
        day: 1,
      }
    );
  }

  private getLatestPaidDataIsoDate(): string | null {
    const monthlyItems = (this.historySourceSig()?.monthlyItems ?? []).filter(
      item => item.premiumSource !== 'projected' && item.year === this.currentYear,
    );
    const period = this.getLatestMonthlyProductionPeriod(monthlyItems);

    return period ? toIsoDate(new Date(period.year, period.month, 0)) : null;
  }

  private getLatestMonthlyProductionPeriod(
    items: ProductionHistoryMonthlyItemResponse[],
  ): { year: number; month: number } | null {
    return items.reduce<{ year: number; month: number } | null>((latestPeriod, item) => {
      if (
        !latestPeriod ||
        item.year > latestPeriod.year ||
        (item.year === latestPeriod.year && item.month > latestPeriod.month)
      ) {
        return { year: item.year, month: item.month };
      }

      return latestPeriod;
    }, null);
  }

  private formatFootnoteDate(isoDate: string): string {
    const parts = parseIsoDateParts(isoDate);
    if (!parts) {
      return isoDate;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(new Date(parts.year, parts.month - 1, parts.day));
  }

  /** Whether the API projects for itself; until it does, the client fills the rest of the year. */
  private usesApiProjectionContract(
    monthlyItems: ProductionHistoryMonthlyItemResponse[],
    quarterlyItems: ProductionHistoryQuarterlyItemResponse[],
    yearlyItems: ProductionHistoryYearlyItemResponse[],
  ): boolean {
    if (this.historySourceSig()?.meta.horizon) {
      return true;
    }

    return (
      hasProductionHistoryProjectionContract(monthlyItems) ||
      hasProductionHistoryProjectionContract(quarterlyItems) ||
      hasProductionHistoryProjectionContract(yearlyItems)
    );
  }

  private hasCurrentYearActualProduction(): boolean {
    const source = this.historySourceSig();
    if (!source) {
      return false;
    }

    return (
      this.hasCurrentYearActualItems(source.monthlyItems) ||
      this.hasCurrentYearActualItems(source.quarterlyItems) ||
      this.hasCurrentYearActualItems(source.yearlyItems)
    );
  }

  private hasCurrentYearActualItems(
    items: Array<ProductionHistoryProjectionFields & { year: number; premium: number }>,
  ): boolean {
    return items.some(item => {
      if (item.year !== this.currentYear) {
        return false;
      }

      // A row the API already projected states its own settled figure separately.
      if (item.isProjected === true || item.premiumSource === 'projected') {
        return typeof item.actualPremium === 'number' && item.actualPremium !== 0;
      }

      return item.premium !== 0;
    });
  }

  /**
   * Seeded from the first load that has data. Re-seeding on later loads would undo the viewer's
   * own year selection every time they changed a filter or the horizon.
   */
  private initializeYearSelectionIfNeeded(data: ProductionHistoryDashboardData): void {
    if (this.yearSelectionInitialized || !this.hasAnyHistoryData({ data, summaryData: {} })) {
      return;
    }

    this.selectedYearsSig.set(this.getHistoryYearRange(data).filter(year => year !== this.currentYear));
    this.yearSelectionInitialized = true;
  }
}
